import React from 'react';
import { NotificationWidget } from '../../NotificationRules/comoponents/NotificationWidget';
import { getNotifRules } from '../../../sp-firebase/notificationRulesFB';
import { Account, IMaterialToAccount } from '../../../sp-types/accountTypes';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { SkeletonList } from './StrategiesUtils';
import { IExposureHistory } from '../../../exposureHistory';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { Button } from '@mui/material';
import { NotificationRule } from '../../../sp-types/notificationRule';
import { OverlayDisplayDiv } from '../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { LoadingSpinnerOverlay } from '../../../components/UI-Elements/LoadingSpinnerOverlay';
import { updateNotifRule } from '../../../sp-firebase/notificationRulesFB';
import { EditNotifRule } from '../../NotificationRules/comoponents/EditNotifRule';

interface ExposureStats {
  safeZonePercentage: number;
  longestOutsideSafeZoneDays: number;
  warningZoneEntries: number;
  totalWarningZoneDays: number;
  criticalZoneEntries: number;
  totalCriticalZoneDays: number;
  totalDays: number;
}
interface Props {
  materialId: string;
  startDate: Date;
  colorsMap: Record<string, string>;
  account: Account;
  exposureHistory?: IExposureHistory[];
  accountMaterial?: IMaterialToAccount;
  onEditStrategy?: () => void;
}
const toMonthStr = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export const StrategyDetails: React.FC<Props> = ({
  materialId,
  startDate,
  colorsMap,
  account,
  exposureHistory,
  accountMaterial,
  onEditStrategy,
}) => {
  const [notifRules, setNotifRules] = React.useState<any[]>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [editRule, setEditRule] = React.useState<NotificationRule | undefined>(undefined);

  const { pricesMap } = useCurrencyRates();
  const fetchNotif = async () => {
    setLoading(true);
    const rules = await getNotifRules(account?.id || '', '', '', 'all');

    setNotifRules(rules);
    setLoading(false);
  };
  // React.useEffect(() => {
  //   console.log('exposureHistory:', exposureHistory);
  // }, [exposureHistory]);

  // Calculate exposure value for historical data points
  const calculateExposureValue = React.useCallback(
    (exposure: IExposureHistory): number => {
      const rawExposure = exposure.futureOrders - exposure.inventory - exposure.financialPosition;
      const priceMultiplier = materialId === GENERAL_ID ? 1 : pricesMap[materialId] || 1;
      return rawExposure * priceMultiplier;
    },
    [materialId, pricesMap],
  );

  // Get risk tolerance ranges converted to proper units
  const riskRanges = React.useMemo(() => {
    if (!accountMaterial) return null;

    const divide = materialId === GENERAL_ID ? 1 : pricesMap[materialId] || 1;
    const { RiskTolerance } = accountMaterial;

    return {
      criticalLow: RiskTolerance.dangerAreaMinus.start / divide,
      warningLow: RiskTolerance.dangerAreaMinus.end / divide,
      safeStart: RiskTolerance.stableArea.start / divide,
      warningHigh: RiskTolerance.dangerAreaPlus.start / divide,
      criticalHigh: RiskTolerance.dangerAreaPlus.end / divide,
    };
  }, [accountMaterial, materialId, pricesMap]);

  // Calculate exposure statistics with proper date differences
  const exposureStats = React.useMemo((): ExposureStats | null => {
    if (!exposureHistory || !riskRanges || exposureHistory.length === 0) {
      return null;
    }

    // Assume exposureHistory is already sorted by date
    if (exposureHistory.length < 2) {
      // Not enough data points to calculate meaningful statistics
      return null;
    }

    let safeZoneDays = 0;
    let warningZoneEntries = 0;
    let totalWarningZoneDays = 0;
    let criticalZoneEntries = 0;
    let totalCriticalZoneDays = 0;
    let longestOutsideSafeZoneDays = 0;

    let previousZone: 'safe' | 'warning' | 'critical' | null = null;
    let currentOutsideSafeStart: Date | null = null;

    // Helper function to calculate days between two dates
    const getDaysDiff = (date1: Date, date2: Date): number => {
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      return Math.ceil(diffTime / (3_600_000 * 24));
    };

    for (let i = 0; i < exposureHistory.length; i++) {
      const currentExposure = exposureHistory[i];
      const currentDate = currentExposure.createDate.toDate();
      const nextExposure = exposureHistory[i + 1]; // This is safe because we check i < length

      // Calculate days this exposure state lasted
      let daysInCurrentState: number;
      if (nextExposure) {
        const nextDate = nextExposure.createDate.toDate();
        daysInCurrentState = getDaysDiff(currentDate, nextDate);
      } else {
        // For the last data point, assume it lasted until today or use minimum of 1 day
        daysInCurrentState = 1; //Math.max(1, getDaysDiff(currentDate, today));
      }

      const exposureValue = calculateExposureValue(currentExposure);

      // Determine current zone
      let currentZone: 'safe' | 'warning' | 'critical';

      if (exposureValue <= riskRanges.criticalLow || exposureValue >= riskRanges.criticalHigh) {
        currentZone = 'critical';
      } else if (
        (exposureValue > riskRanges.criticalLow && exposureValue <= riskRanges.warningLow) ||
        (exposureValue >= riskRanges.warningHigh && exposureValue < riskRanges.criticalHigh)
      ) {
        currentZone = 'warning';
      } else {
        currentZone = 'safe';
      }

      // Count days in each zone
      if (currentZone === 'safe') {
        safeZoneDays += daysInCurrentState;

        // End of outside safe zone streak
        if (currentOutsideSafeStart) {
          const streakDays = getDaysDiff(currentOutsideSafeStart, currentDate);
          longestOutsideSafeZoneDays = Math.max(longestOutsideSafeZoneDays, streakDays);
          currentOutsideSafeStart = null;
        }
      } else {
        // Start tracking outside safe zone streak
        if (!currentOutsideSafeStart) {
          currentOutsideSafeStart = currentDate;
        }

        if (currentZone === 'warning') {
          totalWarningZoneDays += daysInCurrentState;
          // Count entries (when transitioning into warning zone)
          if (previousZone !== 'warning') {
            warningZoneEntries++;
          }
        } else if (currentZone === 'critical') {
          totalCriticalZoneDays += daysInCurrentState;
          // Count entries (when transitioning into critical zone)
          if (previousZone !== 'critical') {
            criticalZoneEntries++;
          }
        }
      }

      previousZone = currentZone;
    }

    // Handle case where we're still in an outside-safe-zone streak
    if (currentOutsideSafeStart) {
      const today = new Date();
      const streakDays = getDaysDiff(currentOutsideSafeStart, today);
      longestOutsideSafeZoneDays = Math.max(longestOutsideSafeZoneDays, streakDays);
    }

    // Calculate total days from first to last data point
    const firstDate = exposureHistory[0].createDate.toDate();
    const lastDate = exposureHistory[exposureHistory.length - 1].createDate.toDate();
    const totalDays = Math.max(1, getDaysDiff(firstDate, lastDate));

    const safeZonePercentage = totalDays > 0 ? Math.round((safeZoneDays / totalDays) * 100) : 0;

    return {
      safeZonePercentage,
      longestOutsideSafeZoneDays,
      warningZoneEntries,
      totalWarningZoneDays,
      criticalZoneEntries,
      totalCriticalZoneDays,
      totalDays,
    };
  }, [exposureHistory, riskRanges, calculateExposureValue]);

  const formatDuration = (days: number): string => {
    if (days === 0) return '0 days';
    if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;

    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    if (remainingDays === 0) {
      return `${weeks} week${weeks === 1 ? '' : 's'}`;
    }
    return `${weeks} week${weeks === 1 ? '' : 's'} and ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
  };

  React.useEffect(() => {
    if (materialId) {
      fetchNotif();
    }
  }, [account]);

  const updateRule = async (rule: NotificationRule) => {
    setLoading(true);

    await updateNotifRule(rule);

    await fetchNotif();
  };

  return loading ? (
    <SkeletonList />
  ) : (
    <div className="flex flex-1 bg-white shadow-md flex-col p-3 gap-2">
      <h3 className="text-lg2 py-2">
        <p>
          Strategy details {materialId !== GENERAL_ID && <span className="text-sm">(material {materialId}) </span>}
          <i className="px-3 fas fa-info-circle" />
        </p>
      </h3>
      <p>
        <b>Statistics</b> - from {toMonthStr(startDate)} to {toMonthStr(new Date())}
        {/* {exposureStats && (
          <span className="text-sm text-gray-500"> ({formatDuration(exposureStats.totalDays)} total)</span>
        )} */}
      </p>

      {/* Exposure Statistics Section */}
      {exposureStats && exposureHistory && exposureHistory.length > 1 && (
        <div className="w-full rounded-lg">
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-300 rounded-full mr-2"></div>
              <span>
                You`ve been in the safe zone <strong>{exposureStats.safeZonePercentage}%</strong> of the time
              </span>
            </div>

            {exposureStats.longestOutsideSafeZoneDays > 0 && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-700 rounded-full mr-2"></div>
                <span>
                  The longest period outside the safe zone lasted{' '}
                  <strong>{formatDuration(exposureStats.longestOutsideSafeZoneDays)}</strong>
                </span>
              </div>
            )}

            {exposureStats.warningZoneEntries > 0 && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-700 rounded-full mr-2"></div>
                <span>
                  You entered the warning zone <strong>{exposureStats.warningZoneEntries}</strong> time
                  {exposureStats.warningZoneEntries === 1 ? '' : 's'}, spending a total of{' '}
                  <strong>{formatDuration(exposureStats.totalWarningZoneDays)}</strong> there
                </span>
              </div>
            )}

            {exposureStats.criticalZoneEntries > 0 && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span>
                  You entered the critical zone <strong>{exposureStats.criticalZoneEntries}</strong> time
                  {exposureStats.criticalZoneEntries === 1 ? '' : 's'}, spending a total of{' '}
                  <strong>{formatDuration(exposureStats.totalCriticalZoneDays)}</strong> there
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <h3 className="text-lg2 mt-3">Rules</h3>
      <div className="flex flex-1 overflow-y-auto">
        <NotificationWidget
          colorsMap={colorsMap}
          rowsState={notifRules?.filter((rule) => materialId === GENERAL_ID || rule.materialId === materialId) || []}
          onEditRule={(rule) => setEditRule(rule)}
        />
      </div>
      {materialId !== GENERAL_ID && (
        <div className="flex flex-0 justify-end">
          <Button variant="outlined" color="primary" onClick={onEditStrategy}>
            Edit strategy
          </Button>
        </div>
      )}
      {editRule && (
        <OverlayDisplayDiv setDisplayOverlay={() => setEditRule(undefined)} hideClose>
          <EditNotifRule rule={editRule} updateRule={updateRule} close={() => setEditRule(undefined)} />
        </OverlayDisplayDiv>
      )}
    </div>
  );
};
